import { useEffect, useRef, useState } from 'react';
import { api, type Brand, type Defaults, type Card, type FilmRequest, type Source, type Turn } from '../lib/api';
import { DEFAULT_BUBBLE, drawSpyglassFrame, easeZoom, frameSize, mixForRecorder, type Bubble, type Zoom } from '../lib/spyglass';
import { readSeconds, scrollFor, takeFormat, takeName, unmark } from '../lib/prompter';
import { reading, readingLine } from '../lib/takes';

/**
 * Record: camera and microphone in the browser, a teleprompter over the
 * picture, takes that land as camera sources.
 *
 * A take is recorded here, uploaded straight to storage the way any source is,
 * normalised by ingest (which also writes its sound beside it as a wav), and
 * then cut into a scene on the Flight Deck with Sound set to "the take
 * speaks". Nothing here touches the script: the prompter shows what is typed
 * or pasted, and the scene's line is where those words live.
 */
const PACE_KEY = 'rookery.prompter.pace';

/**
 * "Add to the video as a scene", usable wherever a take is listed (the Record
 * screen, the Sources table). A new scene at the end of the chosen video: cut
 * to the take, its own sound, the words read on it as the line.
 */
export function AddTakeToVideo({ take, said, brand, cards, onOpenCard, onError, defaultTarget, defaultInto }: {
  take: Source; said?: string; brand: Brand | null; cards: Card[];
  onOpenCard?: (id: string) => void; onError: (m: string) => void;
  /** P3: the scene this take was filmed for, when it was. */
  defaultTarget?: string; defaultInto?: string;
}) {
  const [target, setTarget] = useState(defaultTarget ?? cards[0]?.id ?? '');
  const [scenes, setScenes] = useState<Turn[]>([]);
  const [into, setInto] = useState<string>(defaultInto ?? 'end');       // 'end' or a scene index
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!target && cards[0]) setTarget(cards[0].id); }, [cards, target]);
  useEffect(() => {
    if (!target) { setScenes([]); return; }
    let live = true;
    api.card(target).then((d) => { if (live) { setScenes(d.rundown); setInto(target === defaultTarget && defaultInto ? defaultInto : 'end'); } }).catch(() => undefined);
    return () => { live = false; };
  }, [target]);
  async function add() {
    if (!target) { onError('pick a video to add the take to'); return; }
    setBusy(true);
    try {
      const d = await api.card(target);
      let rundown: Turn[];
      if (into === 'end') {
        // A new scene at the end: cut to the take, its own sound, the words
        // read on it as the line.
        // A take recorded without a line still has a length: it holds for
        // its own duration and plays its own sound; the caption is blank.
        const words = (said ?? '').trim();
        rundown = [...d.rundown, {
          speaker: Object.keys(brand?.voices ?? {})[0] ?? d.speakers[0] ?? 'Host',
          text: words, emotion: 'neutral', gesture: 'none', shot: 'wide',
          beat: 0.35, sourceId: take.id, audio: 'sync',
          ...(words ? {} : { holdSeconds: Math.max(0.5, Math.round(((take.duration_ms ?? 0) / 1000) * 10) / 10 || 5) }),
        }];
      } else {
        // Into an existing scene: that scene now cuts to the take with its own
        // sound; its line stays unless it was empty.
        const i = Number(into);
        rundown = d.rundown.map((t, k) => {
          if (k !== i) return t;
          const text = t.text.trim() || (said ?? '').trim();
          return { ...t, sourceId: take.id, audio: 'sync', text,
                   holdSeconds: text ? undefined : Math.max(0.5, Math.round(((take.duration_ms ?? 0) / 1000) * 10) / 10 || 5) };
        });
      }
      await api.saveRundown(target, rundown);
      // The take joins the episode's collection (093) so the tray and the
      // pickers show it with the rest of what this episode uses.
      await api.collect(target, { sourceId: take.id, note: 'a take' }).catch(() => undefined);
      onOpenCard?.(target);
    } catch (e) { onError((e as Error).message); }
    finally { setBusy(false); }
  }
  if (take.status !== 'ready') return <span className="sub-dim">{take.status === 'error' ? 'failed to ingest' : 'ingesting… a minute or two'}</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={target} onChange={(e) => setTarget(e.target.value)} title="The video this take goes into">
        {cards.map((c) => <option key={c.id} value={c.id}>{c.title.slice(0, 48)}</option>)}
      </select>
      <select value={into} onChange={(e) => setInto(e.target.value)} title="A new scene at the end, or an existing scene that should cut to this take">
        <option value="end">as a new scene at the end</option>
        {scenes.map((t, i) => <option key={i} value={String(i)}>into scene {i + 1} · {(t.kind === 'title' ? 'title' : t.text || 'held').slice(0, 36)}</option>)}
      </select>
      <button className="btn tiny" disabled={busy || !target} onClick={() => void add()}
              title="Cut to this take with its own sound — the words you read become the line of a new scene, or stay the chosen scene's own">
        {busy ? 'Adding…' : into === 'end' ? 'Add to the video as a scene' : 'Put the take in that scene'}
      </button>
    </span>
  );
}

/**
 * Watch a source. A ▶ that fetches a short-lived signed URL and plays the
 * normalised copy inline — the same bytes a scene cuts to — with the native
 * controls. Closes on the same button.
 */
export function SourcePlayer({ source, onError }: { source: Source; onError: (m: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (url) { setUrl(null); return; }
    setBusy(true);
    try { setUrl((await api.sourceMedia(source.id)).media.url); }
    catch (e) { onError((e as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <>
      <button type="button" className="btn tiny" disabled={busy || source.status !== 'ready'} onClick={() => void toggle()}
              title={source.status === 'ready' ? 'Watch it' : 'Playable once ingested'}>{url ? '■ Close' : '▶ Watch'}</button>
      {url && (
        <div style={{ flexBasis: '100%', marginTop: 8 }}>
          <video src={url} controls autoPlay playsInline style={{ width: '100%', maxWidth: 720, borderRadius: 8, background: '#000' }} />
        </div>
      )}
    </>
  );
}

/** A source's thumb, 96 wide, or a quiet box until ingest has made one. */
export function SourceThumb({ source, width = 96 }: { source: Source; width?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    if (source.status !== 'ready') return;
    api.sourceThumbnail(source.id).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [source.id, source.status]);
  return (
    <span style={{ width, height: Math.round(width * 0.5625), borderRadius: 4, background: 'var(--surface-3, #222)', overflow: 'hidden', display: 'inline-block', flex: 'none', verticalAlign: 'middle' }}>
      {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
    </span>
  );
}

/** A take's name, editable in place: click it, type, Enter or click away. */
export function TakeName({ take, onRenamed, onError }: { take: Source; onRenamed: (name: string) => void; onError: (m: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(take.name);
  useEffect(() => setName(take.name), [take.name]);
  async function save() {
    setEditing(false);
    const next = name.trim();
    if (!next || next === take.name) { setName(take.name); return; }
    try { await api.updateSource(take.id, { name: next }); onRenamed(next); }
    catch (e) { onError((e as Error).message); setName(take.name); }
  }
  return editing
    ? <input autoFocus value={name} maxLength={160} onChange={(e) => setName(e.target.value)} onBlur={() => void save()}
             onKeyDown={(e) => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') { setName(take.name); setEditing(false); } }} />
    : <button type="button" className="btn tiny" style={{ fontWeight: 600 }} title="Click to rename" onClick={() => setEditing(true)}>{take.name}</button>;
}

export function Record({ brand, onOpenCard, film = null, startOnScreen = false, defaults }: { brand: Brand | null; onOpenCard?: (id: string) => void; film?: FilmRequest | null; startOnScreen?: boolean; defaults?: Defaults }) {
  const video = useRef<HTMLVideoElement>(null);
  const scriptBox = useRef<HTMLDivElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef<number>(0);
  const frame = useRef<number>(0);

  const [ready, setReady] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [camera, setCamera] = useState<string>('');
  // R4: a second camera — a wide and a close of the same read. Picture only;
  // the sound is the first camera's. Its take is named for the angle.
  const [second, setSecond] = useState<string>('');
  const video2 = useRef<HTMLVideoElement>(null);
  const stream2 = useRef<MediaStream | null>(null);
  const recorder2 = useRef<MediaRecorder | null>(null);
  const chunks2 = useRef<Blob[]>([]);
  const [script, setScript] = useState('');
  const [pace, setPace] = useState<number>(() => { try { return Number(localStorage.getItem(PACE_KEY)) || 140; } catch { return 140; } });
  const [mirror, setMirror] = useState(false);
  const [size, setSize] = useState(40);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Takes are RECORDED here, so only stored sources belong in the list. A
  // Camera source declared under Sources is a live feed waiting on a relay
  // that is not running; it read here as "ingesting… a minute or two" for
  // weeks, which is a lie about something this screen never made.
  const [takes, setTakes] = useState<Source[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [lines, setLines] = useState<Record<string, string>>({});   // what was read on each take
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState('');          // the next take's name, if the creator gives one
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // SN1: Spyglass — the screen with the camera in a bubble. The screen stream and
  // a canvas the two are composed on; the recorder takes the canvas.
  // Spyglass opens the Record screen with the screen mode on; a take made here is its input.
  const [spyglass, setSpyglass] = useState(startOnScreen);
  // 155: the bubble starts where the workspace's defaults put it.
  const [bubble, setBubble] = useState<Bubble>(defaults?.bubble ?? DEFAULT_BUBBLE);
  const bubbleRef = useRef<Bubble>(DEFAULT_BUBBLE);
  useEffect(() => { bubbleRef.current = bubble; }, [bubble]);
  const screen = useRef<MediaStream | null>(null);
  const screenVideo = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const composeFrame = useRef<number>(0);
  const mixer = useRef<{ stream: MediaStream; close: () => void } | null>(null);
  const [sharing, setSharing] = useState(false);
  // SN4: zoom — click a spot on the picture to zoom in on it (2×, then 3×), click again to come back; eased every frame.
  const zoomTarget = useRef<Zoom>({ x: 0.5, y: 0.5, k: 1 });
  const zoomNow = useRef<Zoom>({ x: 0.5, y: 0.5, k: 1 });
  const [zoomK, setZoomK] = useState(1);

  function clickZoom(e: React.MouseEvent<HTMLCanvasElement>) {
    const c = canvas.current; if (!c) return;
    const r = c.getBoundingClientRect();
    // the canvas is letterboxed inside its box (object-fit: contain): map the click to the picture
    const k = Math.min(r.width / c.width, r.height / c.height);
    const dw = c.width * k, dh = c.height * k, ox = (r.width - dw) / 2, oy = (r.height - dh) / 2;
    const x = Math.max(0, Math.min(1, (e.clientX - r.left - ox) / dw)), y = Math.max(0, Math.min(1, (e.clientY - r.top - oy) / dh));
    const next = zoomTarget.current.k >= 3 ? 1 : zoomTarget.current.k >= 2 ? 3 : 2;
    zoomTarget.current = next === 1 ? { x: 0.5, y: 0.5, k: 1 } : { x, y, k: next };
    setZoomK(next);
  }

  async function openScreen() {
    setError(null);
    try {
      screen.current?.getTracks().forEach((t) => t.stop());
      const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 30 } }, audio: true });
      screen.current = s;
      if (screenVideo.current) { screenVideo.current.srcObject = s; await screenVideo.current.play().catch(() => undefined); }
      setSharing(true);
      // the browser's own "stop sharing" bar ends the take cleanly
      s.getVideoTracks()[0]?.addEventListener('ended', () => { setSharing(false); if (recorder.current?.state === 'recording') stop(); });
      compose();
    } catch (e) {
      const name = (e as DOMException).name;
      setError(name === 'NotAllowedError' ? 'no screen was picked. Choose a screen, a window or a tab to share.' : `the screen did not open: ${(e as Error).message}`);
      setSharing(false);
    }
  }

  function closeScreen() {
    cancelAnimationFrame(composeFrame.current);
    screen.current?.getTracks().forEach((t) => t.stop()); screen.current = null;
    setSharing(false);
  }

  /** Draw the screen and the camera onto the canvas, every frame, while Spyglass is on. */
  function compose() {
    cancelAnimationFrame(composeFrame.current);
    const loop = () => {
      const c = canvas.current, sv = screenVideo.current, cv = video.current;
      if (c && sv) {
        const { W, H } = frameSize(sv.videoWidth, sv.videoHeight);
        if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
        const ctx = c.getContext('2d');
        zoomNow.current = easeZoom(zoomNow.current, zoomTarget.current);
        if (ctx) drawSpyglassFrame(ctx, W, H, sv, cv, bubbleRef.current, brand?.accent_hex ?? '#e0b100', mirror, zoomNow.current.k > 1.001 ? zoomNow.current : null);
      }
      composeFrame.current = requestAnimationFrame(loop);
    };
    loop();
  }

  async function openSecond(deviceId: string) {
    stream2.current?.getTracks().forEach((t) => t.stop());
    stream2.current = null;
    if (!deviceId) return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 }, deviceId: { exact: deviceId } }, audio: false });
      stream2.current = s;
      if (video2.current) { video2.current.srcObject = s; await video2.current.play().catch(() => undefined); }
    } catch (e) { setError(`the second camera did not open: ${(e as Error).message}`); setSecond(''); }
  }

  async function open(deviceId?: string) {
    setError(null);
    try {
      stream.current?.getTracks().forEach((t) => t.stop());
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      stream.current = s;
      if (video.current) { video.current.srcObject = s; await video.current.play().catch(() => undefined); }
      setReady(true);
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === 'videoinput'));
    } catch (e) {
      const name = (e as DOMException).name;
      setError(name === 'NotFoundError' || name === 'OverconstrainedError'
        ? 'no camera was found. Plug one in or allow this site to use it, then pick it under Camera.'
        : name === 'NotAllowedError'
          ? 'the browser did not allow the camera. Allow it for app.rookeryos.com and try again.'
          : `the camera did not open: ${(e as Error).message}`);
      setReady(false);
    }
  }

  useEffect(() => {
    void open();
    void api.sources().then((r) => setTakes(r.sources.filter((s) => (s.kind === 'camera' || s.kind === 'screen') && s.mode === 'stored').slice(0, 12))).catch(() => undefined);
    void api.cards().then((r) => setCards(r.cards)).catch(() => undefined);
    return () => { stream.current?.getTracks().forEach((t) => t.stop()); stream2.current?.getTracks().forEach((t) => t.stop()); screen.current?.getTracks().forEach((t) => t.stop()); cancelAnimationFrame(frame.current); cancelAnimationFrame(composeFrame.current); mixer.current?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { try { localStorage.setItem(PACE_KEY, String(pace)); } catch { /* per-viewer convenience */ } }, [pace]);
  // P3: filming a scene — the line goes on the prompter, the take is named
  // after the scene, and it lands in that scene when it is added.
  useEffect(() => {
    if (!film) return;
    setScript(unmark(film.line));   // the marks are for the captions, not the reader
    setLabel(`Scene ${film.sceneIndex + 1}`);
  }, [film]);

  function tick() {
    const t = (performance.now() - startedAt.current) / 1000;
    setElapsed(t);
    const box = scriptBox.current;
    if (box) box.scrollTop = scrollFor(t, script, pace, box.scrollHeight, box.clientHeight);
    frame.current = requestAnimationFrame(tick);
  }

  function start() {
    if (!stream.current) return;
    const fmt = takeFormat((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m));
    // SN1: a Spyglass take records the composed canvas with the microphone (and the tab's sound) — never the bare camera
    let source: MediaStream = stream.current;
    if (spyglass && canvas.current && screen.current) {
      mixer.current?.close();
      mixer.current = mixForRecorder(canvas.current, stream.current, screen.current, 30);
      source = mixer.current.stream;
    }
    const rec = new MediaRecorder(source, fmt.mime ? { mimeType: fmt.mime, videoBitsPerSecond: spyglass ? 6_000_000 : 8_000_000 } : undefined);
    chunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    const n = count;
    rec.onstop = () => void upload(new Blob(chunks.current, { type: fmt.mime || 'video/webm' }), fmt.ext, n, undefined, spyglass && !!screen.current);
    if (stream2.current && !spyglass) {
      const rec2 = new MediaRecorder(stream2.current, fmt.mime ? { mimeType: fmt.mime, videoBitsPerSecond: 8_000_000 } : undefined);
      chunks2.current = [];
      rec2.ondataavailable = (e) => { if (e.data.size) chunks2.current.push(e.data); };
      rec2.onstop = () => void upload(new Blob(chunks2.current, { type: fmt.mime || 'video/webm' }), fmt.ext, n, 'angle B');
      recorder2.current = rec2;
      rec2.start(1000);
    }
    recorder.current = rec;
    rec.start(1000);
    startedAt.current = performance.now();
    if (scriptBox.current) scriptBox.current.scrollTop = 0;
    setRecording(true);
    frame.current = requestAnimationFrame(tick);
  }

  function stop() {
    cancelAnimationFrame(frame.current);
    recorder.current?.stop();
    recorder2.current?.stop();
    setRecording(false);
  }

  async function upload(blob: Blob, ext: string, n: number, angle?: string, isSpyglass = false) {
    setBusy('uploading'); setError(null);
    try {
      const base = label.trim() ? `${label.trim()} — take ${n}` : isSpyglass ? `Spyglass — take ${n}` : takeName(script, n);
      const created = await api.createSource({
        name: angle ? `${base} · ${angle}` : base, kind: isSpyglass ? 'screen' : 'camera', brandId: brand?.id ?? null, mode: 'stored',
        filename: `${isSpyglass ? 'spyglass' : 'take'}-${n}${angle ? '-b' : ''}.${ext}`, contentType: blob.type || `video/${ext}`,
      });
      if (!created.upload) throw new Error('no upload was offered for the take');
      const put = await fetch(created.upload.url, { method: 'PUT', headers: { 'Content-Type': blob.type || `video/${ext}` }, body: blob });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      await api.ingestSource(created.source.id);
      if (script.trim()) { await api.updateSource(created.source.id, { said: script.trim() }).catch(() => undefined); }
      if (!angle) setCount(n + 1);
      setLines((prev) => ({ ...prev, [created.source.id]: script }));
      setTakes((prev) => [created.source, ...prev].slice(0, 12));
      // Ingest takes a minute or two; refresh the list until the take is ready.
      const poll = setInterval(() => {
        api.sources().then((r) => {
          const mine = r.sources.filter((s) => (s.kind === 'camera' || s.kind === 'screen') && s.mode === 'stored').slice(0, 12);
          setTakes(mine);
          if (mine.find((s) => s.id === created.source.id)?.status === 'ready') clearInterval(poll);
        }).catch(() => clearInterval(poll));
      }, 6000);
      setTimeout(() => clearInterval(poll), 5 * 60_000);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  const total = readSeconds(script, pace);
  return (
    <section className="panel">
      <div className="vhead" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Record</h2>
        <span className="sub-dim">{film
          ? <>filming <b>scene {film.sceneIndex + 1}</b> of “{film.title}” — the line is on the prompter; a take you add goes into that scene</>
          : 'a take on camera with the prompter over the picture — it lands in Sources, and a scene cut to it can speak with the take\'s own sound'}</span>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)', gap: 16, marginBlock: 12 }}>
        <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden', aspectRatio: '16 / 9', maxWidth: '100%' }}>
          <video ref={video} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: mirror ? 'scaleX(-1)' : undefined, display: spyglass ? 'none' : undefined }} />
          {/* SN1: the composed picture — the screen with the camera in its bubble; the screen's own video stays hidden and feeds the canvas */}
          <video ref={screenVideo} muted playsInline style={{ display: 'none' }} />
          <canvas ref={canvas} data-tool="spyglass-canvas" onClick={clickZoom} title={zoomK > 1 ? `zoomed ${zoomK}× — click to ${zoomK >= 3 ? 'come back' : 'go closer'}` : 'click a spot to zoom in on it'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: spyglass ? 'block' : 'none', background: '#101418', cursor: spyglass && sharing ? 'zoom-in' : undefined }} />
          {spyglass && sharing && zoomK > 1 && <span style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,.6)', color: 'white', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{zoomK}×</span>}
          {spyglass && !sharing && (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'white', textAlign: 'center', padding: 24 }}>
              <div><p style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Spyglass: your screen, with you in the corner.</p>
                <p className="sub-dim" style={{ margin: '0 0 12px' }}>Pick a screen, a window or a browser tab. A tab shares its sound too.</p>
                <button className="btn pri" data-tool="spyglass-share" onClick={() => void openScreen()}>Choose what to share</button></div>
            </div>
          )}
          {/* The prompter: the script over the picture, the reading line a
              little above centre where the lens is nearest the eyes. */}
          <div ref={scriptBox} aria-label="Prompter"
               style={{ position: 'absolute', inset: 0, overflow: 'hidden', padding: '40% 8% 60%', color: 'white',
                        fontSize: size, lineHeight: 1.35, fontWeight: 600, textShadow: '0 2px 8px rgba(0,0,0,.9)',
                        background: 'linear-gradient(rgba(0,0,0,.55), rgba(0,0,0,.15) 40%, rgba(0,0,0,.55))',
                        whiteSpace: 'pre-wrap', pointerEvents: 'none' }}>
            {script || 'Type or paste the line on the right. It scrolls past this line at your pace while you record.'}
          </div>
          <div style={{ position: 'absolute', left: 0, right: 0, top: '40%', borderTop: '2px solid rgba(220,38,38,.8)', pointerEvents: 'none' }} />
          {recording && (
            <span style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(220,38,38,.95)', color: 'white', padding: '4px 10px', borderRadius: 6, fontWeight: 700 }}>
              ● REC {elapsed.toFixed(0)}s
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <div className="seg" data-tool="record-mode" style={{ display: 'flex', gap: 4 }}>
            <button className={`btn tiny${!spyglass ? ' pri' : ''}`} disabled={recording} onClick={() => { setSpyglass(false); closeScreen(); }}>Camera</button>
            <button className={`btn tiny${spyglass ? ' pri' : ''}`} disabled={recording} title="Spyglass: record your screen — a demo, a webinar, a walk through the browser — with your camera in a bubble" onClick={() => setSpyglass(true)}>Spyglass · screen + camera</button>
          </div>
          {spyglass && (
            <div style={{ display: 'grid', gap: 6, border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px' }} data-tool="spyglass-bubble">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="sub-dim">You sit</span>
                <select value={bubble.corner} onChange={(e) => setBubble({ ...bubble, corner: e.target.value as Bubble['corner'] })}>
                  <option value="bottom-right">bottom right</option><option value="bottom-left">bottom left</option><option value="top-right">top right</option><option value="top-left">top left</option>
                </select>
                <select value={bubble.shape} onChange={(e) => setBubble({ ...bubble, shape: e.target.value as Bubble['shape'] })}>
                  <option value="circle">in a circle</option><option value="rounded">in a rounded box</option>
                </select>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>size <input type="range" min={10} max={45} value={bubble.size} onChange={(e) => setBubble({ ...bubble, size: Number(e.target.value) })} /></label>
                <label><input type="checkbox" checked={!!bubble.hidden} onChange={(e) => setBubble({ ...bubble, hidden: e.target.checked })} /> screen only</label>
              </div>
              {sharing
                ? <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span className="chip">sharing</span><button className="btn tiny" disabled={recording} onClick={closeScreen}>Stop sharing</button><span className="sub-dim">The take is the screen at its own size (up to 1920 wide), with the microphone and the tab's sound.</span></div>
                : <span className="sub-dim">A Spyglass take lands in Sources as a screen recording, transcribed like a camera take, and can be cut into any episode.</span>}
            </div>
          )}
          <label>Camera{' '}
            <select value={camera} disabled={recording} onChange={(e) => { setCamera(e.target.value); void open(e.target.value || undefined); }}>
              <option value="">default</option>
              {devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'camera'}</option>)}
            </select>
          </label>
          {devices.length > 1 && (
            <label title="R4: a second angle of the same read — its take is named 'angle B'; the sound is the first camera's">Second camera{' '}
              <select value={second} disabled={recording} onChange={(e) => { setSecond(e.target.value); void openSecond(e.target.value); }}>
                <option value="">none</option>
                {devices.filter((d) => d.deviceId !== camera).map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'camera'}</option>)}
              </select>
            </label>
          )}
          {second && (
            <div style={{ width: 160, aspectRatio: '16 / 9', background: '#000', borderRadius: 6, overflow: 'hidden' }}>
              <video ref={video2} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: mirror ? 'scaleX(-1)' : undefined }} />
            </div>
          )}
          <label>Name the take
            <input value={label} maxLength={80} placeholder="e.g. Cold open, Spencer's answer — takes are numbered after it"
                   onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label>The line
            <textarea rows={8} value={script} onChange={(e) => setScript(e.target.value)} style={{ width: '100%', resize: 'vertical' }}
                      placeholder="Paste the scene's line here. What you say is what the take will caption." />
          </label>
          <label>Pace <input type="range" min={80} max={220} step={5} value={pace} onChange={(e) => setPace(Number(e.target.value))} />
            <span className="sub-dim"> {pace} words a minute · {total ? `${total.toFixed(0)}s read` : '—'}</span></label>
          <label>Size <input type="range" min={24} max={64} step={2} value={size} onChange={(e) => setSize(Number(e.target.value))} /></label>
          <label><input type="checkbox" checked={mirror} onChange={(e) => setMirror(e.target.checked)} /> mirror the picture (as a mirror would)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {!recording
              ? <button className="btn" disabled={!ready || !!busy || (spyglass && !sharing)} onClick={start}>{busy ? busy : spyglass ? `Record Spyglass take ${count}` : `Record take ${count}`}</button>
              : <button className="btn" onClick={stop} style={{ background: 'var(--red)', color: 'white' }}>Stop</button>}
          </div>
          <div className="sub-dim">
            After a take lands: open the video on the Flight Deck, cut the scene to it, and set Sound to "the take speaks".
          </div>
        </div>
      </div>

      {takes.length > 0 && (
        <div>
          <div className="sub-dim" style={{ marginBottom: 6 }}>Takes — each one can become a scene of a video in one click</div>
          {takes.map((t) => (
            <div key={t.id} className="r" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
              <SourceThumb source={t} />
              <span className="chip">{t.status === 'ready' ? 'ready' : t.status === 'error' ? 'failed' : 'ingesting…'}</span>
              <SourcePlayer source={t} onError={setError} />
              <TakeName take={t} onRenamed={(name) => setTakes((prev) => prev.map((x) => (x.id === t.id ? { ...x, name } : x)))} onError={setError} />
              {t.duration_ms != null && <span className="sub-dim">{(t.duration_ms / 1000).toFixed(1)}s</span>}
              {(lines[t.id] || t.said) && <span className="sub-dim" title="What was said on this take — typed, or heard by the transcriber">“{(lines[t.id] || t.said || '').slice(0, 80)}{(lines[t.id] || t.said || '').length > 80 ? '…' : ''}”</span>}
              {lines[t.id] && t.said && lines[t.id] !== t.said && (
                <span className="sub-dim" title="The prompter's line against what the transcriber heard — the same arithmetic the director uses to place a take">
                  {readingLine(reading(lines[t.id]!, t.said))}
                </span>
              )}
              <span style={{ flex: 1 }} />
              <AddTakeToVideo take={t} said={lines[t.id] ?? t.said ?? ''} brand={brand} cards={cards} onOpenCard={onOpenCard} onError={setError}
                              defaultTarget={film?.cardId} defaultInto={film ? String(film.sceneIndex) : undefined} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
